/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  ElementRef,
} from 'react';
import { TimePickerProps } from '../types';
import { AmPmSelect } from './AmPmSelect';
import UpChevronIcon from '../../../../assets/images/svg/UpChevronIcon';
import DownChevronIcon from '../../../../assets/images/svg/DownChevronIcon';
import { Button } from '../../button';
import {
  nullFn,
  isNumber,
  getHourValue,
  getMinValue,
  getAmPmValue,
  getNewTypedValue,
  normalizeHour,
  normalizeMinutes,
  getNextHour,
  getNextMinute,
  getNextAmPmValue,
  getPrevHour,
  getPrevMinute,
  get24HourTimeNumber,
} from '../utils';
import { generateUID } from '../../../utils';

type TimePickerHandel = {
  setFocusToHourInput: () => void;
};

type AmPmSelectHandel = ElementRef<typeof AmPmSelect>;

export const TimePicker = forwardRef<TimePickerHandel, TimePickerProps>(
  (
    {
      id = undefined,
      onChange = nullFn,
      onCloseModal = nullFn,
      value = undefined,
      className = undefined,
      style = undefined,
      zone = undefined,
    },
    ref,
  ): JSX.Element => {
    const [hourValue, setHourValue] = useState<string>(getHourValue(value));
    const [minValue, setMinValue] = useState<string>(getMinValue(value));
    const [ampmValue, setAmpmValue] = useState<string>(getAmPmValue(value));
    const [inputFocusIdx, setInputFocusIdx] = useState<number>(0);
    const [prevHour, setPrevHour] = useState<string>('');
    const [prevMin, setPrevMin] = useState<string>('');
    const [upOrDownClicked, setUpOrDownClicked] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
      setFocusToHourInput: setFocusToHourInput,
    }));

    const containerRef = useRef<HTMLDivElement>(null);

    const hoursRef = useRef<HTMLInputElement>(null);
    const minsRef = useRef<HTMLInputElement>(null);
    const ampmRef = useRef<AmPmSelectHandel>(null);
    const inputMap: Record<
      number,
      React.RefObject<HTMLInputElement | AmPmSelectHandel>
    > = {
      0: hoursRef,
      1: minsRef,
      2: ampmRef,
    };

    function preventDefault<T, E>(event: React.SyntheticEvent<T, E>) {
      event.preventDefault();
    }

    const setFocusToHourInput = () => {
      if (hoursRef.current) {
        hoursRef.current.focus();
        setInputFocusIdx(0);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      const { key } = event;

      switch (key) {
        case 'ArrowRight': {
          handleArrowRightPressed();
          return;
        }
        case 'ArrowLeft': {
          handleArrowLeftPressed();
          return;
        }
        case 'ArrowUp': {
          handleButtonUpClick();
          return;
        }
        case 'ArrowDown': {
          handleButtonDownClick();
          return;
        }
        case 'Delete': {
          handleDeletePressed();
          return;
        }
        case 'Backspace': {
          handleDeletePressed();
          return;
        }
        case 'Enter': {
          handleOnChangeTime();
          return;
        }
        default: {
          return;
        }
      }
    };

    const handleArrowRightPressed = () => {
      const nextInputIdx = inputFocusIdx + 1;
      const nextInputFocus = inputMap[nextInputIdx];
      if (nextInputFocus && nextInputFocus.current !== null) {
        setInputFocusIdx(inputFocusIdx + 1);
        nextInputFocus.current.focus();
      }
    };

    const handleArrowLeftPressed = () => {
      const prevInputIdx = inputFocusIdx - 1;
      const prevInputFocus = inputMap[prevInputIdx];
      if (prevInputFocus && prevInputFocus.current !== null) {
        setInputFocusIdx(inputFocusIdx - 1);
        prevInputFocus.current.focus();
      }
    };

    const handleDeletePressed = () => {
      if (inputFocusIdx === 0) {
        setHourValue('');
        setPrevHour('');
      }

      if (inputFocusIdx === 1) {
        setMinValue('');
        setPrevMin('');
      }
    };

    const handleOnHourChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      let normalized: string;

      if (isNumber(value)) {
        if (Number(value) > 23 || upOrDownClicked) {
          const newTyped = getNewTypedValue(hourValue, value);
          normalized = normalizeHour(newTyped);
          setHourValue(normalized);
        } else {
          normalized = normalizeHour(value);
          setHourValue(normalized);
        }

        const shouldNotFocusNextInput =
          Number(normalized) < 2 && (!hourValue || upOrDownClicked);

        if (shouldNotFocusNextInput) {
          setUpOrDownClicked(false);
          return;
        }

        if (minsRef.current) {
          minsRef.current.focus();
        }
      }
    };

    const handleOnMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      let normalized: string;

      if (isNumber(value)) {
        if (Number(value) > 60) {
          const newTyped = getNewTypedValue(minValue, value);
          normalized = normalizeMinutes(newTyped);
          setMinValue(normalized);
        } else {
          normalized = normalizeMinutes(value);
          setMinValue(normalized);
        }

        const shouldNotFocusNextInput =
          Number(normalized) < 6 && (!minValue || upOrDownClicked);

        if (shouldNotFocusNextInput) {
          setUpOrDownClicked(false);
          return;
        }

        if (ampmRef.current) {
          ampmRef.current.focus();
        }
      }
    };

    const handleHourBlur = () => {
      if (upOrDownClicked) {
        setUpOrDownClicked(false);
        return;
      }

      if (hourValue === '00' && value && value.hour >= 0) {
        setHourValue('12');
      }
    };

    const handleHourFocus = () => {
      setPrevHour(hourValue || prevHour);
      setHourValue('');
      setInputFocusIdx(0);
    };

    const handleMinutesFocus = () => {
      setPrevMin(minValue || prevMin);
      setMinValue('');
      setInputFocusIdx(1);
    };

    const handleAmPmFocus = () => {
      setInputFocusIdx(2);
    };

    const handleAmPmChange = (value?: string) => {
      setAmpmValue(value || '');
    };

    const handleAmPmClick = () => {
      setInputFocusIdx(-1);
    };

    const handleButtonUpClick = () => {
      setUpOrDownClicked(true);
      if (inputFocusIdx === 0) {
        addHour();
      }
      if (inputFocusIdx === 1) {
        addMinute();
      }
      if (inputFocusIdx === 2) {
        setNextAmPmValue();
      }
    };

    const addHour = () => {
      const hour = hourValue || prevHour;
      setHourValue(normalizeHour(getNextHour(hour)));
    };

    const addMinute = () => {
      const mins = minValue || prevMin;
      setMinValue(normalizeMinutes(getNextMinute(mins)));
    };

    const setNextAmPmValue = () => {
      const nextValue = getNextAmPmValue(ampmValue);
      setAmpmValue(nextValue);
    };

    const handleButtonDownClick = () => {
      setUpOrDownClicked(true);
      if (inputFocusIdx === 0) {
        minusHour();
      }
      if (inputFocusIdx === 1) {
        minusMin();
      }
      if (inputFocusIdx === 2) {
        setNextAmPmValue();
      }
    };

    const minusHour = () => {
      const hour = hourValue || prevHour;
      setHourValue(normalizeHour(getPrevHour(hour)));
    };

    const minusMin = () => {
      const mins = minValue || prevMin;
      setMinValue(normalizeMinutes(getPrevMinute(mins)));
    };

    const handleButtonsDown = () => {
      setUpOrDownClicked(true);
    };

    const handleButtonsFocus = () => {
      if (inputFocusIdx === -1) return;
      const currentInputFocused = inputMap[inputFocusIdx];
      if (currentInputFocused.current) {
        currentInputFocused.current.focus();
      }
    };

    const handleOnChangeTime = () => {
      const currentHour = hourValue || prevHour;
      const currentMin = minValue || prevMin;
      const time = get24HourTimeNumber({
        hour: currentHour,
        minutes: currentMin,
        ampm: ampmValue,
        zone: zone,
      });

      if (currentHour && currentMin && ampmValue) {
        onChange(time);
      }

      onCloseModal();
    };

    return (
      <div className={className} style={style}>
        <div
          ref={containerRef}
          role='presentation'
          onKeyDown={handleKeyDown}
          className='flex appearance-none items-center justify-between rounded-lg border border-gray-light px-5 py-4 text-base font-medium placeholder-gray-light shadow-sm focus:outline-none'
        >
          <div className='flex w-5/6 flex-row items-center justify-between space-x-4'>
            <input
              aria-label='hours'
              ref={hoursRef}
              onPaste={preventDefault}
              onDrop={preventDefault}
              type='text'
              value={hourValue}
              placeholder={prevHour ? prevHour : '--'}
              inputMode='numeric'
              onChange={handleOnHourChange}
              onFocus={handleHourFocus}
              onBlur={handleHourBlur}
              pattern='\d*'
              className='h-16 w-full rounded-lg border border-gray-light text-center text-2xl placeholder-gray-light focus:border-transparent focus:bg-green-lightest focus:outline-none focus:ring-2 focus:ring-green'
            />
            <span className='block text-2xl text-gray-darkest'>:</span>
            <input
              aria-label='minutes'
              ref={minsRef}
              type='text'
              onPaste={preventDefault}
              onDrop={preventDefault}
              value={minValue}
              placeholder={prevMin ? prevMin : '--'}
              inputMode='numeric'
              pattern='\d*'
              className='h-16 w-full rounded-lg border border-gray-light text-center text-2xl placeholder-gray-light focus:border-transparent focus:bg-green-lightest focus:outline-none focus:ring-2 focus:ring-green'
              onChange={handleOnMinChange}
              onFocus={handleMinutesFocus}
            />
            <AmPmSelect
              tabIndex={-1}
              id={id ? id : generateUID()}
              value={ampmValue}
              ref={ampmRef}
              onFocus={handleAmPmFocus}
              isFocused={inputFocusIdx === 2}
              onClick={handleAmPmClick}
              onChange={handleAmPmChange}
            />
          </div>

          <div className='my-0 flex h-16 w-1/6 flex-col'>
            <button
              id='timePicker-btn-up'
              tabIndex={-1}
              type='button'
              className='flex w-full items-center justify-center text-green'
              onClick={handleButtonUpClick}
              onMouseDown={handleButtonsDown}
              onFocus={handleButtonsFocus}
            >
              <UpChevronIcon width='36px' height='36px' />
            </button>
            <button
              id='timePicker-btn-down'
              tabIndex={-1}
              type='button'
              className='flex w-full items-center justify-center text-green'
              onClick={handleButtonDownClick}
              onMouseDown={handleButtonsDown}
              onFocus={handleButtonsFocus}
            >
              <DownChevronIcon width='36px' height='36px' />
            </button>
          </div>
        </div>
        <div className='mt-4 flex flex-row items-center justify-end space-x-3'>
          <Button
            label='Cancel'
            theme='SMALL_SECONDARY'
            onClick={onCloseModal}
          />
          <Button
            label='Ok'
            theme='SMALL_PRIMARY'
            onClick={handleOnChangeTime}
          />
        </div>
      </div>
    );
  },
);
